import { ChevronLeft, ChevronRight, ExternalLink, Star } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { api } from "@/api";

export function GoogleLogo({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-label="Google" role="img">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z" />
    </svg>
  );
}

export function Stars({ value, size = 16 }) {
  return (
    <span className="rv-stars" aria-label={`${value} sur 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className="rv-star" style={{ width: size, height: size }}>
          <Star size={size} className="rv-star-bg" />
          <span className="rv-star-fill" style={{ width: `${Math.max(0, Math.min(1, value - i + 1)) * 100}%` }}><Star size={size} /></span>
        </span>
      ))}
    </span>
  );
}

function ReviewCard({ r }) {
  return (
    <article className="rv-card" data-testid="review-card">
      <header><span className="rv-avatar">{r.author.trim()[0]?.toUpperCase()}</span><div><strong>{r.author}</strong>{r.date && <small>{r.date}</small>}</div><GoogleLogo size={18} /></header>
      <Stars value={r.rating} size={15} />
      <p>{r.text}</p>
    </article>
  );
}

export function ReviewsSection() {
  const [data, setData] = useState(null);
  const track = useRef(null);
  useEffect(() => { api.get("/public/reviews").then(({ data: d }) => setData(d)).catch(() => {}); }, []);
  if (!data) return null;
  const scroll = (dir) => track.current?.scrollBy({ left: dir * track.current.clientWidth * 0.8, behavior: "smooth" });
  const rating = Number(data.rating).toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return (
    <section id="avis" className="site-section rv-section" data-testid="reviews-section">
      <div className="rv-head">
        <div>
          <p className="site-kicker">Avis clients</p>
          <h2>Ils nous ont fait confiance</h2>
        </div>
        <div className="rv-summary" data-testid="reviews-summary">
          <GoogleLogo size={34} />
          <div>
            <div className="rv-score"><strong data-testid="reviews-rating">{rating}</strong><Stars value={Number(data.rating)} size={20} /></div>
            <p>Note moyenne sur Google{data.count > 0 && <> · <span data-testid="reviews-count">{data.count} avis</span></>}</p>
          </div>
          <a href={data.url} target="_blank" rel="noopener noreferrer" className="rv-google-btn" data-testid="reviews-google-link">Voir les avis sur Google <ExternalLink size={14} /></a>
        </div>
      </div>
      {data.reviews.length > 0 && (
        <div className="rv-carousel">
          <div className="rv-track" ref={track} data-testid="reviews-track">{data.reviews.map((r, i) => <ReviewCard key={`${r.author}-${i}`} r={r} />)}</div>
          {data.reviews.length > 3 && <div className="rv-arrows"><button type="button" onClick={() => scroll(-1)} aria-label="Avis précédents" data-testid="reviews-prev"><ChevronLeft size={18} /></button><button type="button" onClick={() => scroll(1)} aria-label="Avis suivants" data-testid="reviews-next"><ChevronRight size={18} /></button></div>}
        </div>
      )}
    </section>
  );
}
